import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Smartphone,
    MessageSquare,
    CheckCircle2,
    ArrowRight,
    Clock,
    Plus,
    Trash2,
    Copy,
    Check,
    Zap,
    TrendingUp,
    Shield,
    Bell,
    QrCode,
    HelpCircle,
    ChevronDown
} from "lucide-react";
import SEO from "../components/SEO";
import AppSlider from "../components/AppSlider";
import { useRegion } from "../context/RegionContext";

export default function MobileAccounting() {
    const { region } = useRegion();

    // Interactive Invoicing & WhatsApp Collection States
    const [customerName, setCustomerName] = useState("Alex Morgan");
    const [countryDialCode, setCountryDialCode] = useState(region.phonePrefix || "+1");
    const [customerPhone, setCustomerPhone] = useState("4155552671");
    const [invoiceNo, setInvoiceNo] = useState("INV-2026-089");
    const [dueDate, setDueDate] = useState("2026-09-10");
    const [taxRate, setTaxRate] = useState(region.defaultTaxRate || 8.25);
    const [reminderTone, setReminderTone] = useState("friendly"); // friendly, due_today, overdue, invoice
    const [copied, setCopied] = useState(false);

    // Sync tax rate, phone prefix, and default sample items when region is changed
    useEffect(() => {
        setTaxRate(region.defaultTaxRate);
        setCountryDialCode(region.phonePrefix);
        const isRupee = region.currency === "INR";
        setItems([
            { id: 1, name: "Consulting & Financial Advisory", qty: 1, price: isRupee ? 4500 : 350 },
            { id: 2, name: "Monthly Bookkeeping & Tax Prep", qty: 1, price: isRupee ? 3500 : 250 },
        ]);
    }, [region]);

    const isINR = region.currency === "INR";
    const [items, setItems] = useState([
        { id: 1, name: "Consulting & Financial Advisory", qty: 1, price: isINR ? 4500 : 350 },
        { id: 2, name: "Monthly Bookkeeping & Tax Prep", qty: 1, price: isINR ? 3500 : 250 },
    ]);

    const addItem = () => {
        setItems([...items, { id: Date.now(), name: "Professional Services", qty: 1, price: isINR ? 1000 : 100 }]);
    };

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(items.filter((item) => item.id !== id));
        }
    };

    const updateItem = (id, field, value) => {
        setItems(items.map((item) => {
            if (item.id === id) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const subtotal = items.reduce((acc, item) => acc + (Number(item.qty) || 0) * (Number(item.price) || 0), 0);
    const taxAmount = Math.round((subtotal * (Number(taxRate) || 0)) / 100);
    const totalAmount = subtotal + taxAmount;

    // Generate formatted WhatsApp message based on active mode
    const generateWhatsAppMessage = () => {
        const formattedTotal = `${region.symbol}${totalAmount.toLocaleString(region.locale)}`;
        const itemsList = items.map((it, idx) => `${idx + 1}. ${it.name} (Qty: ${it.qty}) - ${region.symbol}${(it.qty * it.price).toLocaleString(region.locale)}`).join("\n");

        if (reminderTone === "invoice") {
            return `🧾 *INVOICE: ${invoiceNo}*\n\n` +
                `Hello *${customerName}*,\n` +
                `Here is your invoice for recent services from *blinqbooks*.\n\n` +
                `📋 *Summary:*\n${itemsList}\n\n` +
                `💰 *Subtotal:* ${region.symbol}${subtotal.toLocaleString(region.locale)}\n` +
                `📊 *${region.taxName} (${taxRate}%):* ${region.symbol}${taxAmount.toLocaleString(region.locale)}\n` +
                `✅ *Total Due:* *${formattedTotal}*\n` +
                `📅 *Due Date:* ${dueDate}\n\n` +
                `📲 *Pay via Card/Apple Pay/Stripe:* https://pay.blinqbooks.com/pay/${invoiceNo}\n\n` +
                `Thank you for doing business with us! 🙏`;
        }

        if (reminderTone === "friendly") {
            return `👋 Hi *${customerName}*,\n\n` +
                `This is a gentle payment reminder regarding Invoice *#${invoiceNo}* for *${formattedTotal}*.\n\n` +
                `📅 *Due Date:* ${dueDate}\n` +
                `💳 *Instant Payment Link:* https://pay.blinqbooks.com/pay/${invoiceNo}\n\n` +
                `If you've already completed this payment, please disregard this message. Thank you! ✨`;
        }

        if (reminderTone === "due_today") {
            return `🔔 *Payment Due Today — Invoice #${invoiceNo}*\n\n` +
                `Dear *${customerName}*,\n` +
                `Your pending invoice of *${formattedTotal}* is scheduled for settlement today (*${dueDate}*).\n\n` +
                `⚡ *Click to Pay Instantly (Apple Pay, Cards & Local Rails):*\nhttps://pay.blinqbooks.com/pay/${invoiceNo}\n\n` +
                `Let us know if you need any assistance! 🙏`;
        }

        if (reminderTone === "overdue") {
            return `⚠️ *URGENT: Overdue Payment Notice*\n\n` +
                `Dear *${customerName}*,\n` +
                `Invoice *#${invoiceNo}* for the amount of *${formattedTotal}* is currently *OVERDUE* (Due on: ${dueDate}).\n\n` +
                `Please clear the pending balance today to avoid service interruptions:\n` +
                `👉 *Instant Payment Link:* https://pay.blinqbooks.com/pay/${invoiceNo}\n\n` +
                `If paid already, please share transaction reference. Thank you.`;
        }

        return "";
    };

    const whatsAppMessage = generateWhatsAppMessage();
    const cleanCode = countryDialCode.replace(/\D/g, "");
    const cleanNumber = customerPhone.replace(/\D/g, "");
    const cleanPhone = `${cleanCode}${cleanNumber}`;
    const whatsAppUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(whatsAppMessage)}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(whatsAppMessage);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    // SEO Schemas
    const schemas = [
        {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "blinqbooks Mobile Accounting & WhatsApp Invoicing",
            "applicationCategory": "BusinessApplication, AccountingApplication",
            "operatingSystem": "iOS, Android, Web",
            "description": "Send GST invoices and automated WhatsApp payment collection reminders straight from your mobile phone with blinqbooks."
        }
    ];

    const [openFaq, setOpenFaq] = useState(null);
    const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

    const faqList = [
        {
            q: "Can I send bills directly from my phone without downloading software?",
            a: "Yes! blinqbooks runs seamlessly on your mobile browser as well as native mobile apps. You can create GST/non-GST bills and send them to your customer's WhatsApp in under 30 seconds."
        },
        {
            q: "How do WhatsApp payment collection reminders work?",
            a: "blinqbooks allows you to trigger 1-click WhatsApp reminders or schedule automated friendly, due-date, and overdue payment nudges with direct UPI payment links embedded in each message."
        },
        {
            q: "Can customers pay directly from WhatsApp, Email, or SMS invoices?",
            a: "Yes. Invoices sent via blinqbooks include instant payment links supporting Apple Pay, Google Pay, Credit/Debit cards (Visa, Mastercard, Amex), Stripe, and local bank transfers. Once paid, the payment is automatically reconciled in your accounting ledger."
        },
        {
            q: "Is it compliant with local tax authorities (Sales Tax, VAT, GST)?",
            a: "Absolutely. blinqbooks automatically handles tax calculations compliant with US state sales taxes, UK HMRC Making Tax Digital, EU VAT OSS, UAE FTA VAT, and Canadian / Indian GST with complete digital audit trails."
        }
    ];

    return (
        <div className="min-h-screen bg-base text-primary transition-colors duration-300 font-sans">
            <SEO
                title="Mobile Accounting & WhatsApp Invoicing | Automated Payment Collection"
                description="Create bills on your phone and send instant invoices & automated payment reminders on WhatsApp. Collect dues 3x faster with blinqbooks mobile accounting."
                canonical="https://www.blinqbooks.com/features/mobile-billing"
                keywords="mobile accounting, whatsapp invoicing, whatsapp payment collection, phone billing app, mobile gst invoice, automated payment reminders, small business billing, blinqbooks"
                ogImage="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&q=80&w=1200"
                ogType="website"
                schema={schemas}
            />

            {/* Hero Section */}
            <section className="relative py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
                {/* Background ambient lighting */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-full max-w-4xl h-80 bg-gradient-to-tr from-emerald-500/15 via-blue-500/10 to-teal-500/15 blur-[120px] pointer-events-none rounded-full -z-10"></div>

                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-6 border border-emerald-500/20 shadow-sm"
                    >
                        <MessageSquare className="w-4 h-4 text-emerald-500" />
                        WhatsApp Invoicing & Mobile Accounting
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.15] mb-6"
                    >
                        Bill on Mobile. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-blue-600 dark:from-emerald-400 dark:via-teal-300 dark:to-blue-400">
                            Collect Faster on WhatsApp.
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed font-medium mb-8"
                    >
                        Create GST invoices on your phone in 30 seconds. Send bills, track views, and trigger automated WhatsApp payment collection reminders with instant UPI links.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4"
                    >
                        <a
                            href="#live-demo"
                            className="btn-primary w-full sm:w-auto px-8 py-4 text-base sm:text-lg flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20"
                        >
                            Try Interactive Generator <ArrowRight className="w-5 h-5" />
                        </a>
                        <Link
                            to="/signup"
                            className="w-full sm:w-auto px-9 py-4 rounded-full font-extrabold text-base sm:text-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 shadow-xl shadow-blue-600/25 transition-all duration-300 text-center block cursor-pointer"
                        >
                            Start Free Trial ✨
                        </Link>
                    </motion.div>

                    {/* Trust Badges */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> 1-Click WhatsApp Bills</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
                        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Auto Collection Reminders</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
                        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Instant UPI QR Links</span>
                    </div>
                </div>
            </section>

            {/* LIVE INTERACTIVE GENERATOR & WHATSAPP SIMULATOR */}
            <section id="live-demo" className="py-12 md:py-20 px-4 sm:px-6 lg:px-8 bg-surface border-y border-slate-200/80 dark:border-slate-800">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider">
                            Interactive Live Tool
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mt-3 mb-4">
                            Test Mobile Invoicing & WhatsApp Delivery
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
                            Edit the invoice details below and test sending real WhatsApp invoices or collection reminders.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        {/* Left: Mobile Invoice Builder Card */}
                        <div className="lg:col-span-6 bg-white dark:bg-slate-900 rounded-3xl p-5 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-xl space-y-6">
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                        <Smartphone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Mobile Invoice Creator</h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Instant GST & non-GST billing</p>
                                    </div>
                                </div>
                                <span className="px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                                    Live Sync
                                </span>
                            </div>

                            {/* Customer Information */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Customer Name</label>
                                    <input
                                        type="text"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-sm font-semibold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                                        placeholder="e.g. Alex Morgan"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Phone / WhatsApp</label>
                                    <div className="flex gap-2">
                                        <select
                                            value={countryDialCode}
                                            onChange={(e) => setCountryDialCode(e.target.value)}
                                            className="px-2.5 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-xs font-bold text-slate-900 dark:text-white outline-none shrink-0"
                                        >
                                            <option value="+1">🇺🇸/🇨🇦 +1</option>
                                            <option value="+44">🇬🇧 +44</option>
                                            <option value="+49">🇩🇪 +49</option>
                                            <option value="+33">🇫🇷 +33</option>
                                            <option value="+971">🇦🇪 +971</option>
                                            <option value="+966">🇸🇦 +966</option>
                                            <option value="+61">🇦🇺 +61</option>
                                            <option value="+91">🇮🇳 +91</option>
                                        </select>
                                        <input
                                            type="text"
                                            value={customerPhone}
                                            onChange={(e) => setCustomerPhone(e.target.value)}
                                            className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-sm font-semibold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none font-mono"
                                            placeholder="4155552671"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Meta */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Invoice #</label>
                                    <input
                                        type="text"
                                        value={invoiceNo}
                                        onChange={(e) => setInvoiceNo(e.target.value)}
                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-xs font-bold text-slate-900 dark:text-white font-mono"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Due Date</label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-xs font-bold text-slate-900 dark:text-white"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{region.taxName} Rate</label>
                                    <select
                                        value={taxRate}
                                        onChange={(e) => setTaxRate(Number(e.target.value))}
                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-white/5 text-xs font-bold text-slate-900 dark:text-white"
                                    >
                                        {region.taxOptions.map((opt, i) => (
                                            <option key={i} value={opt.rate}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Itemized Lines */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Bill Line Items</label>
                                    <button
                                        onClick={addItem}
                                        className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Add Item
                                    </button>
                                </div>

                                <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                                    {items.map((item, idx) => (
                                        <div key={item.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-slate-800">
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateItem(item.id, "name", e.target.value)}
                                                className="flex-1 min-w-0 bg-transparent text-xs font-semibold text-slate-900 dark:text-white outline-none"
                                                placeholder="Item / Service Name"
                                            />
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.qty}
                                                    onChange={(e) => updateItem(item.id, "qty", Math.max(1, parseInt(e.target.value) || 1))}
                                                    className="w-12 px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 text-xs font-bold text-center bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                                    title="Quantity"
                                                />
                                                <span className="text-xs text-slate-400">×</span>
                                                <div className="relative flex items-center">
                                                    <span className="absolute left-1.5 text-[10px] text-slate-400 font-mono">{region.symbol}</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={item.price}
                                                        onChange={(e) => updateItem(item.id, "price", Math.max(0, parseInt(e.target.value) || 0))}
                                                        className="w-24 pl-5 pr-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 text-xs font-bold text-right bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-mono"
                                                        title="Price"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    disabled={items.length <= 1}
                                                    className="p-1 rounded text-slate-400 hover:text-red-500 disabled:opacity-30 cursor-pointer"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total Calculation Display */}
                            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-100 dark:border-slate-800/80 space-y-2">
                                <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 font-medium">
                                    <span>Subtotal</span>
                                    <span className="font-mono">{region.symbol}{subtotal.toLocaleString(region.locale)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 font-medium">
                                    <span>{region.taxName} ({taxRate}%)</span>
                                    <span className="font-mono">{region.symbol}{taxAmount.toLocaleString(region.locale)}</span>
                                </div>
                                <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center text-sm font-bold text-slate-900 dark:text-white">
                                    <span>Total Invoice Amount</span>
                                    <span className="text-lg font-black text-emerald-600 dark:text-emerald-400 font-mono">
                                        {region.symbol}{totalAmount.toLocaleString(region.locale)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Live WhatsApp Preview & Action Panel */}
                        <div className="lg:col-span-6 space-y-6">
                            {/* Reminder Type Switcher */}
                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                                        <Bell className="w-4 h-4 text-emerald-500" /> WhatsApp Message Mode
                                    </span>
                                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full">
                                        4 Templates Ready
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {[
                                        { id: "invoice", label: "New Invoice", color: "blue" },
                                        { id: "friendly", label: "Gentle Reminder", color: "emerald" },
                                        { id: "due_today", label: "Due Today", color: "amber" },
                                        { id: "overdue", label: "Overdue Dues", color: "rose" },
                                    ].map((mode) => (
                                        <button
                                            key={mode.id}
                                            onClick={() => setReminderTone(mode.id)}
                                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all text-center cursor-pointer ${reminderTone === mode.id
                                                ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                                                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-750"
                                                }`}
                                        >
                                            {mode.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Simulated WhatsApp Phone Chat Interface */}
                                <div className="rounded-2xl bg-[#e5ddd5] dark:bg-[#0b141a] p-4 border border-slate-300 dark:border-slate-800 font-sans shadow-inner relative overflow-hidden">
                                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-black/10 dark:border-white/10">
                                        <div className="w-8 h-8 rounded-full bg-[#25D366] text-white flex items-center justify-center font-bold text-xs">
                                            BB
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1">
                                                {customerName || "Customer"}
                                                <span className="text-[10px] text-emerald-600 font-normal">({customerPhone})</span>
                                            </div>
                                            <div className="text-[10px] text-slate-500 dark:text-slate-400">Online • WhatsApp Business API</div>
                                        </div>
                                    </div>

                                    {/* Message Bubble */}
                                    <div className="bg-white dark:bg-[#1f2c34] text-slate-900 dark:text-slate-100 rounded-2xl rounded-tl-sm p-4 text-xs leading-relaxed shadow-sm border border-black/5 dark:border-white/5 space-y-1.5 whitespace-pre-line font-mono">
                                        {whatsAppMessage}
                                        <div className="text-[9px] text-slate-400 text-right pt-1">
                                            Just now • Sent via blinqbooks ✓✓
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                    <a
                                        href={whatsAppUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 py-3.5 px-6 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md active:scale-[0.98] cursor-pointer"
                                    >
                                        <MessageSquare className="w-4 h-4 fill-white" />
                                        Send on WhatsApp Now
                                    </a>
                                    <button
                                        onClick={handleCopy}
                                        className="px-5 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-750 transition-all cursor-pointer"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                        {copied ? "Copied!" : "Copy Text"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Benefits Grid */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mb-4">
                        Why Modern Businesses Collect on WhatsApp
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg">
                        Emails get lost in spam. Phone calls get ignored. WhatsApp messages have a 98% open rate and get invoices settled in hours.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    {[
                        {
                            icon: <Zap className="w-6 h-6 text-amber-500" />,
                            title: "30-Second Mobile Invoicing",
                            desc: "Generate clean, professional GST & retail invoices right from your phone browser without installing heavy software."
                        },
                        {
                            icon: <MessageSquare className="w-6 h-6 text-emerald-500" />,
                            title: "1-Click WhatsApp PDF Sharing",
                            desc: "Deliver invoices, GST breakdowns, and download links straight into customer WhatsApp chats with zero friction."
                        },
                        {
                            icon: <Clock className="w-6 h-6 text-blue-500" />,
                            title: "Automated Payment Reminders",
                            desc: "Set automated collection schedules. blinqbooks politely follows up before and after due dates so you get paid on time."
                        },
                        {
                            icon: <QrCode className="w-6 h-6 text-purple-500" />,
                            title: "Global Online Payment Links",
                            desc: "Embed dynamic checkout links supporting Apple Pay, Google Pay, Stripe, Credit/Debit cards, and local instant rails."
                        },
                        {
                            icon: <TrendingUp className="w-6 h-6 text-teal-500" />,
                            title: "70% Faster Cash Collection",
                            desc: "Businesses using blinqbooks automated collection reduce overdue receivables from 35 days down to less than 7 days."
                        },
                        {
                            icon: <Shield className="w-6 h-6 text-rose-500" />,
                            title: "Real-Time Auto Reconciliation",
                            desc: "When a customer pays, your general ledger, bank feeds, inventory, and local tax reports are updated automatically in real time."
                        }
                    ].map((feat, i) => (
                        <div
                            key={i}
                            className="p-6 sm:p-8 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 w-fit mb-5">
                                {feat.icon}
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2.5">{feat.title}</h3>
                            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{feat.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Comparison Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-500/[0.02] dark:bg-white/[0.01] border-t border-slate-200/60 dark:border-slate-800/60">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-3">
                            Traditional Billing vs blinqbooks WhatsApp Accounting
                        </h2>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">See the direct impact on your cash collection cycle</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm border-collapse rounded-2xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                            <thead>
                                <tr className="bg-slate-100/70 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-800 text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-bold">
                                    <th className="p-4 sm:p-5">Feature</th>
                                    <th className="p-4 sm:p-5 text-slate-500 dark:text-slate-400">Old Email/Paper Way</th>
                                    <th className="p-4 sm:p-5 text-emerald-600 dark:text-emerald-400">blinqbooks WhatsApp Flow</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                                <tr>
                                    <td className="p-4 sm:p-5 font-bold">Invoice Delivery Time</td>
                                    <td className="p-4 sm:p-5 text-slate-500">Hours / Days (Email Spam)</td>
                                    <td className="p-4 sm:p-5 font-bold text-emerald-600 dark:text-emerald-400">⚡ Under 5 Seconds</td>
                                </tr>
                                <tr>
                                    <td className="p-4 sm:p-5 font-bold">Customer Open Rate</td>
                                    <td className="p-4 sm:p-5 text-slate-500">18% - 22% (Email)</td>
                                    <td className="p-4 sm:p-5 font-bold text-emerald-600 dark:text-emerald-400">🔥 98% (Instant WhatsApp)</td>
                                </tr>
                                <tr>
                                    <td className="p-4 sm:p-5 font-bold">Payment Follow-Up</td>
                                    <td className="p-4 sm:p-5 text-slate-500">Manual awkward phone calls</td>
                                    <td className="p-4 sm:p-5 font-bold text-emerald-600 dark:text-emerald-400">🤖 Automated Polished Reminders</td>
                                </tr>
                                <tr>
                                    <td className="p-4 sm:p-5 font-bold">Average Collection Time</td>
                                    <td className="p-4 sm:p-5 text-slate-500">25 - 45 Days</td>
                                    <td className="p-4 sm:p-5 font-bold text-emerald-600 dark:text-emerald-400">✨ 2 - 7 Days</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Interactive All Apps Slider */}
            <section className="bg-surface border-b border-slate-200/80 dark:border-slate-800">
                <AppSlider
                    title="Connect WhatsApp Billing with the Complete Ecosystem"
                    subtitle="Seamlessly sync mobile invoices with AI accounting, expense tracking, retail POS, and payroll."
                />
            </section>

            {/* FAQ Accordion */}
            <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
                <div className="text-center mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider mb-4 border border-emerald-500/20 shadow-sm">
                        <HelpCircle className="w-4 h-4 text-emerald-500" /> Got Questions?
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight mb-4 leading-tight">
                        Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-500 to-blue-600 dark:from-emerald-400 dark:via-teal-300 dark:to-blue-400">Questions</span>
                    </h2>
                    <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg max-w-xl mx-auto font-medium">
                        Everything you need to know about mobile invoicing, WhatsApp delivery, and automated payment collection.
                    </p>
                </div>

                <div className="space-y-4">
                    {faqList.map((faq, idx) => {
                        const isOpen = openFaq === idx;
                        return (
                            <div
                                key={idx}
                                className={`group rounded-2xl overflow-hidden transition-all duration-300 border ${isOpen
                                    ? "border-emerald-500/50 dark:border-emerald-500/50 bg-white dark:bg-slate-900 shadow-lg shadow-emerald-500/5 ring-1 ring-emerald-500/20"
                                    : "border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900/90 hover:border-emerald-500/40 dark:hover:border-emerald-500/40 shadow-sm hover:shadow-md"
                                    }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleFaq(idx)}
                                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors cursor-pointer"
                                >
                                    <span className="text-base sm:text-lg pr-4 font-extrabold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                                        {faq.q}
                                    </span>
                                    <div
                                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen
                                            ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/30 rotate-180"
                                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-200 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/60 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                                            }`}
                                    >
                                        <ChevronDown className="w-5 h-5" />
                                    </div>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: "easeOut" }}
                                        >
                                            <div className="px-5 sm:px-6 pb-6 pt-2 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed border-t border-slate-100 dark:border-slate-800/80">
                                                {faq.a}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Bottom CTA Banner */}
            <section className="py-20 px-4">
                <div className="max-w-5xl mx-auto bg-gradient-to-tr from-emerald-600 via-teal-600 to-blue-600 rounded-3xl p-8 sm:p-14 text-center text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-400/20 rounded-full blur-[80px] -ml-32 -mb-32"></div>

                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6 relative z-10 leading-tight">
                        Start Billing on WhatsApp in 60 Seconds
                    </h2>
                    <p className="text-emerald-50 mb-8 text-base sm:text-lg max-w-xl mx-auto relative z-10 font-medium">
                        Join thousands of smart businesses automating their invoices and speeding up cash collection with blinqbooks.
                    </p>
                    <Link
                        to="/signup"
                        className="px-10 py-4 bg-white text-emerald-700 font-bold rounded-full text-base sm:text-lg hover:scale-105 hover:shadow-xl transition-all inline-flex items-center gap-2 relative z-10 shadow-lg"
                    >
                        Create Free Account <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
